import { memo, useEffect, useRef } from 'react';
import type { BookingDTO } from '@six-pockets/shared';
import { BookingRow } from './BookingRow';
import styles from '@/styles/components/BookingList/style.scss';

interface BookingListProps {
  tableId: string;
  bookings: BookingDTO[];
  selectedIds: readonly string[];
}

/** A card's bookings. Memoised; each row gets primitive props, so a checkbox re-renders only its own row. */
export const BookingList = memo(function BookingList({ tableId, bookings, selectedIds }: BookingListProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const shownCount = useRef(0);

  // Bookings run earliest first, so the live one is at the bottom. Scroll to it when the card first shows and
  // whenever a booking is added; other updates (timer ticks, renames) leave the scroll position alone.
  useEffect(() => {
    const el = scrollerRef.current;
    if (el && bookings.length > shownCount.current) el.scrollTop = el.scrollHeight;
    shownCount.current = bookings.length;
  }, [bookings.length]);

  return (
    <div className={styles.wrapper}>
      {bookings.length === 0 ? (
        <p className={styles.empty}>Press START to begin a booking</p>
      ) : (
        <div ref={scrollerRef} className={styles.scroller}>
          <div className={styles.columns} aria-hidden>
            <span />
            <span />
            <span className={styles.nameCol}>Name</span>
            <span>Start</span>
            <span>End</span>
            <span>Duration</span>
            <span className={styles.right}>Amount</span>
          </div>
          <ul className={styles.list}>
            {bookings.map((b) => (
              <BookingRow key={b.id} tableId={tableId} booking={b} selected={selectedIds.includes(b.id)} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
});
